import { Alert, Modal, SubmitButton } from "@xola/ui-kit";
import { FormField, InputField, requiredEmail, requiredString } from "common";
import { Field, FieldProps } from "formik";
import React, { useState } from "react";
import { Except } from "type-fest";
import { StripeBillingDetails, Traveler } from "types";
import { object, string } from "yup";
import { CountrySelect } from "../Common/CountrySelect";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";

const validationSchema = object({
    name: requiredString(),
    email: requiredEmail(),
    address: object({
        city: requiredString(),
        country: requiredString(),
        line1: requiredString(),
        line2: string().trim(),
        state: requiredString(),
    }),
});

interface StripeAdditionalBillingDetailsModalProps {
    isOpen: boolean;
    organizer?: Traveler;
    isSubmitting: boolean;
    onSubmit: (values: StripeBillingDetails) => void;
    onClose: () => void;
}

export const StripeAdditionalBillingDetailsModal = (props: StripeAdditionalBillingDetailsModalProps) => (
    <Modal isOpen={props.isOpen} onClose={props.onClose}>
        <ModalContent {...props} />
    </Modal>
);

const ModalContent = ({
    onClose,
    organizer,
    onSubmit,
    isSubmitting = false,
}: Except<StripeAdditionalBillingDetailsModalProps, "isOpen">) => {
    const [showInitialError, setShowInitialError] = useState(true);

    const initialValues: StripeBillingDetails = {
        email: organizer?.email ?? "",
        name: organizer?.name ?? "",
        address: { city: "", country: "", line1: "", line2: "", state: "" },
    };

    const handleSubmit = (values: StripeBillingDetails) => {
        setShowInitialError(false);
        onSubmit(values);
    };

    return (
        <>
            <Modal.Header>Additional Billing Info Required</Modal.Header>

            <XolaFormik
                validateOnChange={false}
                validateOnBlur={false}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit }) => (
                    <>
                        <Modal.Body>
                            {showInitialError ? (
                                <Alert color="danger" className="mb-4 p-3 text-left">
                                    <p className="text-danger">
                                        Sorry, we were unable to process your payment. Please provide the billing
                                        details for your credit card and we will try again.
                                    </p>
                                </Alert>
                            ) : null}

                            <fieldset disabled={isSubmitting}>
                                <FormField name="name" label="Name">
                                    <InputField isRequired placeholder="Name" />
                                </FormField>

                                <FormField name="email" label="Email">
                                    <InputField isRequired placeholder="Email" />
                                </FormField>

                                <FormField name="address.line1" label="Billing Address">
                                    <InputField isRequired placeholder="Address" />
                                </FormField>

                                <FormField name="address.city" label="City">
                                    <InputField isRequired placeholder="City" />
                                </FormField>

                                <FormField name="address.state" label="State">
                                    <InputField isRequired placeholder="State" />
                                </FormField>

                                <Field name="address.country">
                                    {({ form, meta }: FieldProps) => (
                                        <CountrySelect
                                            label="Country"
                                            error={meta.error}
                                            onChange={(option) => {
                                                form.setFieldValue("address.country", option.value);
                                            }}
                                        />
                                    )}
                                </Field>
                            </fieldset>
                        </Modal.Body>

                        <Modal.Footer>
                            <fieldset disabled={isSubmitting} className="flex justify-end space-x-2">
                                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                                <SubmitButton type="submit" isLoading={isSubmitting} onClick={handleSubmit}>
                                    Charge
                                </SubmitButton>
                            </fieldset>
                        </Modal.Footer>
                    </>
                )}
            </XolaFormik>
        </>
    );
};
